import React, { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { useNavigate } from "react-router-dom";

import { LOGIN_MUTATION } from "./graphql/redhawkLogin";
import { SIMWB_LOGIN_MUTATION } from "./graphql/simwbLogin";
import { PORTAL_LOGIN_MUTATION } from "./graphql/login";

import "./login.css";

// Redhawk types
interface RedhawkLoginData {
  login: {
    id: number;
    name: string;
    email: string;
    user_type_id?: string;
    user_status_id?: string;
    verificationToken: string;
  };
}

interface RedhawkLoginVars {
  login: string;
  password: string;
}

// SIMWB types
interface SimwbLoginData {
  simwbLogin: {
    id: number;
    name: string;
    login: string;
    email: string;
    verificationToken: string;
  };
}

interface SimwbLoginVars {
  email: string;
  password: string;
}

// Portal types
interface PortalLoginData {
  portalLogin: {
    id: number;
    name: string;
    email: string;
    login: string;
    system_role?: string;
  };
}

interface PortalLoginVars {
  login: string;
  password: string;
}

type LoginType = "redhawk" | "simwb" | "portal";

const Login: React.FC = () => {
  const navigate = useNavigate();

  const [loginType, setLoginType] =
    useState<LoginType>("redhawk");

  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");

  const [redhawkLogin, {
    loading: redhawkLoading,
    error: redhawkError,
  }] = useMutation<RedhawkLoginData, RedhawkLoginVars>(
    LOGIN_MUTATION
  );

  const [simwbLogin, {
    loading: simwbLoading,
    error: simwbError,
  }] = useMutation<SimwbLoginData, SimwbLoginVars>(
    SIMWB_LOGIN_MUTATION
  );

  const [portalLogin, {
    loading: portalLoading,
    error: portalError,
  }] = useMutation<PortalLoginData, PortalLoginVars>(
    PORTAL_LOGIN_MUTATION
  );

  const loading =
    redhawkLoading ||
    simwbLoading ||
    portalLoading;

  const error =
    redhawkError ||
    simwbError ||
    portalError;

  const changeLoginType = (type: LoginType) => {
    setLoginType(type);
    setLogin("");
    setPassword("");
  };

  const handleLogin = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    try {
      // Existing Portal User
      if (loginType === "portal") {
        const result = await portalLogin({
          variables: {
            login,
            password,
          },
        });

        if (result.data?.portalLogin) {
          localStorage.setItem(
            "user",
            JSON.stringify(result.data.portalLogin)
          );

          localStorage.removeItem("legacyUser");

          const params = new URLSearchParams(window.location.search);
const redirect = params.get("redirect");

window.location.href =
  redirect?.startsWith("/") && !redirect.startsWith("//")
    ? redirect
    : "/portal";
          return;
        }
      }

      // Redhawk
      if (loginType === "redhawk") {
        const result = await redhawkLogin({
          variables: {
            login,
            password,
          },
        });

        if (result.data?.login) {
          localStorage.setItem(
            "legacyUser",
            JSON.stringify({
              ...result.data.login,
              system: "REDHAWK",
            })
          );

          navigate("/signup");
          return;
        }
      }

      // SIMWB
      if (loginType === "simwb") {
        const result = await simwbLogin({
          variables: {
            email: login,
            password,
          },
        });

        if (result.data?.simwbLogin) {
          localStorage.setItem(
            "legacyUser",
            JSON.stringify({
              ...result.data.simwbLogin,
              system: "SIMWB",
            })
          );

          navigate("/signup");
          return;
        }
      }

      alert("Login failed");
    } catch (err) {
      console.error(err);
    }
  };

  const loginLabel =
    loginType === "redhawk"
      ? "Business Email"
      : loginType === "simwb"
      ? "Business Email"
      : "Portal Username";

  return (
    <div className="login-page">

      {/* LEFT SIDE */}
      <section className="login-brand-panel">

        <div className="brand-content">

          {/* Replace with your actual logo */}
          <div className="portal-logo">
            <img src="https://concurrent-rt.com/wp-content/uploads/2025/10/ccrt_eu_logo.png" alt="Logo" />
          </div>

          <div className="welcome-content">
            <h1>
              Welcome To The Concurrent Real-Time
              <br />
              Customer Portal
            </h1>

            <p>
              This site provides you access to Concurrent Real-Time
              software products and updates.
            </p>
          </div>

          <div className="portal-features">

            <Feature
              title="Download the Latest Releases"
              description="Find the current drivers and os repositories"
            />

            <Feature
              title="Product Knowledge Base"
              description="Find documentation for Redhawk Linux and Simulation WorkBench"
            />

            <Feature
              title="Submit Support Tickets"
              description="Submit a request for assistance at any time."
            />

          </div>

        </div>

        <div className="login-footer">
          <span>
            © 2017 - 2026 Concurrent Real-Time - All Rights Reserved
          </span>

          <div className="footer-links">
            <a href="https://concurrent-rt.com/privacy-policy/" target="_blank" rel="noreferrer" >Privacy Policy</a>
            <a href="https://concurrent-rt.com/legal/" target="_blank" rel="noreferrer"  >Legal</a>
            <a href="https://concurrent-rt.com/compliance/" target="_blank" rel="noreferrer" >Compliance</a>
          </div>
        </div>

      </section>

      {/* RIGHT SIDE */}
      <section className="login-form-panel">

        <div className="login-container">

          <div className="login-heading">
            <h2>Login Below</h2>

            <p>
              Please authenticate using your organization's
              security credentials.
            </p>
          </div>

          {/* LOGIN TYPE TABS */}
          <div className="login-tabs">

            <button
              type="button"
              className={
                loginType === "redhawk"
                  ? "login-tab active"
                  : "login-tab"
              }
              onClick={() => changeLoginType("redhawk")}
            >
              Redhawk
            </button>

            <button
              type="button"
              className={
                loginType === "simwb"
                  ? "login-tab active"
                  : "login-tab"
              }
              onClick={() => changeLoginType("simwb")}
            >
              SIMWB
            </button>

            <button
              type="button"
              className={
                loginType === "portal"
                  ? "login-tab active"
                  : "login-tab"
              }
              onClick={() => changeLoginType("portal")}
            >
              Existing Portal User
            </button>

          </div>

          {/* FORM */}
          <form
            className="portal-login-form"
            onSubmit={handleLogin}
          >

            <div className="login-field">
              <label htmlFor="login">
                {loginLabel}
                <span>*</span>
              </label>

              <input
                id="login"
                type={
                  loginType === "simwb"
                    ? "email"
                    : "text"
                }
                value={login}
                onChange={(e) =>
                  setLogin(e.target.value)
                }
                required
                autoComplete="username"
              />
            </div>

            <div className="login-field">
              <label htmlFor="password">
                Password
                <span>*</span>
              </label>

              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) =>
                  setPassword(e.target.value)
                }
                required
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div className="login-error">
                Login failed. Please check your credentials.
              </div>
            )}

            <button
              className="login-submit"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                "Logging in..."
              ) : (
                <>
                  Login
                  <span className="login-arrow">→</span>
                </>
              )}
            </button>

          </form>

          <div className="social-section">
            <span>
              Connect with Concurrent Real-Time
            </span>

            <a
              href="https://www.linkedin.com/company/concurrent-rt/"
              target="_blank"
              rel="noopener noreferrer"
              className="social linkedin"
              aria-label="Concurrent Real-Time LinkedIn"
            >
              in
            </a>

            <a
              href="https://www.youtube.com/@concurrentreal-time"
              target="_blank"
              rel="noopener noreferrer"
              className="social youtube"
              aria-label="Concurrent Real-Time YouTube"
            >
              ▶
            </a>
          </div>

        </div>

      </section>

    </div>
  );
};

interface FeatureProps {
  title: string;
  description: string;
}

const Feature: React.FC<FeatureProps> = ({
  title,
  description,
}) => {
  return (
    <div className="portal-feature">

      <div className="feature-check">
        ✓
      </div>

      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>

    </div>
  );
};

export default Login;